import { useState, useEffect, useCallback } from 'react';
import { IonPage, IonContent, IonSpinner, IonToolbar, IonButton, IonButtons, IonIcon, IonRefresher, IonRefresherContent, RefresherEventDetail } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { refreshOutline, businessOutline } from 'ionicons/icons';
import { BadgesModal, CertificationsModal, ActivityLogModal, UserCoursesModal, TeamMemberTable, TeamMemberList, Widgets, InviteTeamModal, RemovedMembersModal } from '../components/team';
import { useIsMobile } from '../hooks/useMediaQuery';
import { TeamMember, TeamAgent, TeamSummary as TeamSummaryType } from '../types/teamSummary';
import { teamSummary } from '../services/teamSummary';
import { useProfileStore } from '../store/useProfileStore';
import { loadTeamFromProfile, createTeam, getRemovedUsers } from '../services/teamService';
import { ProfileSummary } from '../types/profile-summary';
import { whoAmI } from '../services/authService';
import { getActiveProfile } from '../services/profileService';
import { isTeamOwnerOrAdmin, ProfileStatus } from '../types/profile';
import { getAgentById, updateAgent } from '../services/mysteryShopService';
import { PopoverMenuComponent } from '../components/ui/PopoverMenuComponent';
import { showError } from '../services/toastService';
import { useRefresh } from '../contexts/RefreshContext';
import {
  showTeamNameSaved,
  showTeamNameSaveFailed,
  showMemberSuspendFailed,
  showMemberActivateFailed,
  showMemberRemoveFailed,
  showDataRefreshed,
  showDataRefreshFailed,
} from '../services/toastService';
import { useAccountForm, getAccountFormUrl } from '../utils/accounts';

export function MyTeamPage() {
  const [teamData, setTeamData] = useState<TeamSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { activeTeam } = useProfileStore();
  const router = useIonRouter();
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();

  // Check if user is admin, redirect if not
  useEffect(() => {
    const profile = getActiveProfile();
    if (profile && profile.businessId && !isTeamOwnerOrAdmin(profile)) {
      router.push('/dashboard', 'none');
    }
  }, [router]);

  // Load team from profile on mount
  useEffect(() => {
    const loadTeam = async () => {
      await loadTeamFromProfile();
    };
    loadTeam();
  }, []);

  // Fetch team table data
    const fetchTeamData = async () => {
      try {
        setLoading(true);
        setError(null);
      const data = await teamSummary.getTeamSummary();
        setTeamData(data);
        
        try {
          const removed = await getRemovedUsers();
          setRemovedMembersCount(removed.length);
        } catch (err) {
          setRemovedMembersCount(0);
        }
      } catch (err) {
        // Error modal presented by API
        setError('Failed to load team data');
      } finally {
        setLoading(false);
      }
    };

  // Refetch data when navigating to this page
  useEffect(() => {
    fetchTeamData();
  }, [activeTeam?.id, router.routeInfo.pathname]);

  // Calculate used seats: count Active, Pending, and Suspended users (exclude Removed/Deleted)
  // Suspended users still use seats - only removed users free seats
  const usedSeatsCount = teamData?.users && teamData.users.length > 0
    ? teamData.users.filter(user =>
        user.status === ProfileStatus.Active || 
        user.status === ProfileStatus.Pending || 
        user.status === ProfileStatus.Suspended
      ).length
    : 0;

  // Calculate available seats
  const availableSeats = (teamData?.seats?.totalSeats || 0) - usedSeatsCount;

  // Modal state for mobile and desktop
  const [selectedMemberForBadges, setSelectedMemberForBadges] = useState<TeamMember | null>(null);
  const [selectedMemberForCerts, setSelectedMemberForCerts] = useState<TeamMember | null>(null);
  const [selectedMemberForActivityLog, setSelectedMemberForActivityLog] = useState<TeamMember | null>(null);
  const [selectedMemberForCourses, setSelectedMemberForCourses] = useState<TeamMember | null>(null);
  const [isBadgesModalOpen, setIsBadgesModalOpen] = useState(false);
  const [isCertsModalOpen, setIsCertsModalOpen] = useState(false);
  const [isActivityLogModalOpen, setIsActivityLogModalOpen] = useState(false);
  const [isCoursesModalOpen, setIsCoursesModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isRemovedMembersModalOpen, setIsRemovedMembersModalOpen] = useState(false);
  const [removedMembers, setRemovedMembers] = useState<ProfileSummary[]>([]);
  const [removedMembersCount, setRemovedMembersCount] = useState<number | null>(null);
  const [associatingAgent, setAssociatingAgent] = useState<TeamAgent | null>(null);
  const [showAssociatePopover, setShowAssociatePopover] = useState(false);
  const [associatePopoverEvent, setAssociatePopoverEvent] = useState<Event | undefined>();

  const handleViewBadges = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedMemberForBadges(member);
    setIsBadgesModalOpen(true);
  };

  const handleViewCertifications = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedMemberForCerts(member);
    setIsCertsModalOpen(true);
  };

  const handleCloseBadgesModal = () => {
    setIsBadgesModalOpen(false);
    setSelectedMemberForBadges(null);
  };

  const handleCloseCertsModal = () => {
    setIsCertsModalOpen(false);
    setSelectedMemberForCerts(null);
  };

  const handleViewActivityLog = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedMemberForActivityLog(member);
    setIsActivityLogModalOpen(true);
  };

  const handleCloseActivityLogModal = () => {
    setIsActivityLogModalOpen(false);
    setSelectedMemberForActivityLog(null);
  };

  const handleViewCourses = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedMemberForCourses(member);
    setIsCoursesModalOpen(true);
  };

  const handleCloseCoursesModal = () => {
    setIsCoursesModalOpen(false);
    setSelectedMemberForCourses(null);
  };

  const handlePrintDownloadCourses = async () => {
    if (selectedMemberForCourses) {
      await teamSummary.printDownload('pdf');
    }
  };

  const handleInviteTeamClick = () => {
    if (!activeTeam && useAccountForm()) {
      const accountFormUrl = getAccountFormUrl();
      if (accountFormUrl) {
        window.open(accountFormUrl, '_blank');
      }
    } else {
      setIsInviteModalOpen(true);
    }
  };

  const handleSaveTeamName = async (name: string) => {
    // Prevent duplicate team creation
    if (activeTeam) {
      return;
    }

    try {
      const response = await createTeam(name);

      await whoAmI();

      await showTeamNameSaved();

      setIsInviteModalOpen(false);
    } catch (err) {
      await showTeamNameSaveFailed(err instanceof Error ? err.message : undefined);
    }
  };

  // Action handlers
  const handleSuspend = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await teamSummary.suspend(member);
      // Refresh data after successful suspend
      const data = await teamSummary.getTeamSummary();
      setTeamData(data);
    } catch (err) {
      await showMemberSuspendFailed(err instanceof Error ? err.message : undefined);
    }
  };

  const handleActivate = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await teamSummary.activate(member);
      // Refresh data after successful activate
      const data = await teamSummary.getTeamSummary();
      setTeamData(data);
    } catch (err) {
      await showMemberActivateFailed(err instanceof Error ? err.message : undefined);
    }
  };

  const handleRemove = async (event: React.MouseEvent, member: TeamMember) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await teamSummary.remove(member);
      // Refresh data after successful remove
      const data = await teamSummary.getTeamSummary();
      setTeamData(data);
      
      try {
        const removed = await getRemovedUsers();
        setRemovedMembersCount(removed.length);
      } catch (err) {
        setRemovedMembersCount(0);
      }
    } catch (err) {
      await showMemberRemoveFailed(err instanceof Error ? err.message : undefined);
    }
  };

  const handleAssociateAgentToUser = (event: React.MouseEvent, agent: TeamAgent) => {
    event.preventDefault();
    event.stopPropagation();
    setAssociatingAgent(agent);
    setAssociatePopoverEvent(event.nativeEvent);
    setShowAssociatePopover(true);
  };

  const handleAssociatePopoverDismiss = async (selectedKey?: string) => {
    setShowAssociatePopover(false);
    if (!selectedKey || !associatingAgent) {
      setAssociatingAgent(null);
      return;
    }
    try {
      const fullAgent = await getAgentById(associatingAgent.id);
      fullAgent.profileId = selectedKey;
      await updateAgent(fullAgent);
      // Refresh team summary so agent moves from agents list to member's agentPerformance
      const data = await teamSummary.getTeamSummary();
      setTeamData(data);
    } catch (err) {
      showError('Failed to associate agent to user.');
    } finally {
      setAssociatingAgent(null);
    }
  };

  const handlePrintDownload = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    await teamSummary.printDownload('pdf');
  };

  const handleViewReport = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    await teamSummary.viewReport();
  };

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await teamSummary.getTeamSummary();
      setTeamData(data);
      await showDataRefreshed();
    } catch (err) {
      // Error modal presented by API
      setError('Failed to refresh team data');
      await showDataRefreshFailed(err instanceof Error ? err.message : undefined);
    } finally {
      setLoading(false);
    }
  }, []);

  // Register refresh handler for Test Panel
  useEffect(() => {
    registerRefreshHandler('/my-team', handleRefresh);
    return () => {
      unregisterRefreshHandler('/my-team');
    };
  }, [registerRefreshHandler, unregisterRefreshHandler, handleRefresh]);

  const handleRefreshPull = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      setError(null);
      const data = await teamSummary.getTeamSummary();
      setTeamData(data);
      await showDataRefreshed();
    } catch (err) {
      // Error modal presented by API
      setError('Failed to refresh team data');
      await showDataRefreshFailed(err instanceof Error ? err.message : undefined);
    } finally {
      setLoading(false);
      event.detail.complete();
    }
  };

  return (
    <IonPage>
      <IonContent className="page-content bg-gray-50">
        <IonRefresher slot="fixed" onIonRefresh={handleRefreshPull}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <div className={`page-header ${isMobile ? 'page-header-mobile' : 'page-header-desktop'}`}>
          {activeTeam?.name ? (
            <div className="flex items-center gap-3">
              {activeTeam.logoUrl ? (
                <img 
                  src={activeTeam.logoUrl} 
                  alt={`${activeTeam.name} logo`}
                  className="w-10 h-10 rounded-lg object-cover bg-white/20"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <IonIcon icon={businessOutline} className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="title">{activeTeam.name}</div>
            </div>
          ) : (
          <div className="title">My Team</div>
          )}
        </div>

        <div data-tour="my-team-widgets">
          <Widgets 
            data={teamData ? {
              seats: {
                usedSeats: usedSeatsCount,
                totalSeats: teamData.seats.totalSeats
              },
              metrics: teamData.metrics,
              report: teamData.report
            } : undefined}
            onTeamDataRefresh={fetchTeamData}
          />
        </div>

        <div data-tour="my-team-members">
        <IonToolbar className="bg-white shadow-sm border-b border-gray-200">
          {teamData && teamData.users ? (
            <div slot="start" className="flex items-center px-4">
              <span className="text-[#231F20] font-semibold text-base">
                {usedSeatsCount} {usedSeatsCount === 1 ? 'member' : 'members'}
              </span>
            </div>
          ) : (
            <div slot="start" className="flex items-center px-4">
              <span className="text-[#928881] text-base">No Team Users</span>
            </div>
          )}
          <IonButtons slot="end" className="px-2">
            {/* TODO: Uncomment when View Report feature is implemented
            {activeTeam?.name && (
              <IonButton
                color="primary" fill="solid"
                onClick={(e) => handleViewReport(e as any)}
                disabled={!teamData || !teamData.users}
              >
                View Report
              </IonButton>
            )}
            */}
            <IonButton onClick={handleRefresh} color="secondary" fill="clear" className="toolbar-button-icon" shape="round">
              <IonIcon icon={refreshOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>

        {/* Loading State */}
        {loading && activeTeam && (
          <div className="flex justify-center items-center py-8">
            <IonSpinner name="crescent" />
            <span>Loading team data...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div>
            {error}
          </div>
        )}

        {/* No Active Team */}
        {!activeTeam && !loading && (
          <div className="flex flex-col justify-center items-center py-8 gap-4">
            <p>Create a Team and invite your team members.</p>
            <IonButton color="primary" onClick={handleInviteTeamClick}>
              Create A Team
            </IonButton>
          </div>
        )}

        {/* No Team Users */}
        {teamData && !teamData.users && !loading && (
          <div className="flex flex-col justify-center items-center py-8 gap-4">
            <p>Invite your team members.</p>
            <IonButton color="primary" onClick={handleInviteTeamClick}>
              + Invite your team
            </IonButton>
          </div>
        )}

        {/* Data Loaded */}
        {teamData && teamData.users && teamData.users.length > 0 && !loading && activeTeam?.name && (
          <>
            {isMobile ? (
              <TeamMemberList
                users={teamData.users}
                agents={teamData.agents}
                shop={teamData.shop}
                onViewBadges={(e, member) => handleViewBadges(e, member)}
                onViewCertifications={(e, member) => handleViewCertifications(e, member)}
                onRemove={(e, member) => handleRemove(e, member)}
                onSuspend={(e, member) => handleSuspend(e, member)}
                onActivate={(e, member) => handleActivate(e, member)}
                onViewActivityLog={(e, member) => handleViewActivityLog(e, member)}
                onViewCourses={(e, member) => handleViewCourses(e, member)}
                onAssociateAgentToUser={(e, agent) => handleAssociateAgentToUser(e, agent)}
              />
            ) : (
              <TeamMemberTable
                users={teamData.users}
                agents={teamData.agents}
                shop={teamData.shop}
                onViewBadges={(e, member) => handleViewBadges(e, member)}
                onViewCertifications={(e, member) => handleViewCertifications(e, member)}
                onRemove={(e, member) => handleRemove(e, member)}
                onSuspend={(e, member) => handleSuspend(e, member)}
                onActivate={(e, member) => handleActivate(e, member)}
                onViewActivityLog={(e, member) => handleViewActivityLog(e, member)}
                onViewCourses={(e, member) => handleViewCourses(e, member)}
                onAssociateAgentToUser={(e, agent) => handleAssociateAgentToUser(e, agent)}
              />
            )}
            <div className="flex justify-end px-4 py-3">
              <IonButton
                size="small"
                fill="outline"
                disabled={removedMembersCount === 0 || removedMembersCount === null}
                style={{
                  '--border-color': '#000000',
                  '--color': '#000000',
                  '--border-width': '1px',
                  '--border-style': 'solid'
                } as React.CSSProperties}
                onClick={async () => {
                  try {
                    const removed = await getRemovedUsers();
                    setRemovedMembers(removed);
                    setIsRemovedMembersModalOpen(true);
                  } catch (err) {
                    console.error('Error fetching removed members:', err);
                  }
                }}
              >
                View Removed Members
              </IonButton>
            </div>
          </>
        )}
        </div>

        {/* Modals - Shared between mobile and desktop */}
        {selectedMemberForBadges && (
          <BadgesModal
            member={selectedMemberForBadges}
            isOpen={isBadgesModalOpen}
            onClose={handleCloseBadgesModal}
          />
        )}
        {selectedMemberForCerts && (
          <CertificationsModal
            member={selectedMemberForCerts}
            isOpen={isCertsModalOpen}
            onClose={handleCloseCertsModal}
          />
        )}
        {selectedMemberForActivityLog && (
          <ActivityLogModal
            member={selectedMemberForActivityLog}
            isOpen={isActivityLogModalOpen}
            onClose={handleCloseActivityLogModal}
          />
        )}
        {selectedMemberForCourses && (
          <UserCoursesModal
            member={selectedMemberForCourses}
            isOpen={isCoursesModalOpen}
            onClose={handleCloseCoursesModal}
            onPrintDownload={handlePrintDownloadCourses}
          />
        )}

        {/* Invite Team Modal */}
        <InviteTeamModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          usedSeats={usedSeatsCount}
          totalSeats={activeTeam?.activePlan?.totalSeats || 0}
          teamName={activeTeam?.name}
          teamLogoUrl={activeTeam?.logoUrl}
          seatPriceMonthly={activeTeam?.activePlan?.seatPriceMonthly}
          seatPriceAnnual={activeTeam?.activePlan?.seatPriceAnnual}
          onSaveTeamName={handleSaveTeamName}
          onInvitationsSent={fetchTeamData}
        />

        {/* Associate Agent to User Popover */}
        <PopoverMenuComponent
          isOpen={showAssociatePopover}
          event={associatePopoverEvent}
          alignment="end"
          onDismiss={handleAssociatePopoverDismiss}
          menuItems={(teamData?.users ?? []).map(u => ({ key: u.id, value: u.displayName }))}
        />

        {/* Removed Members Modal */}
        <RemovedMembersModal
          isOpen={isRemovedMembersModalOpen}
          onClose={() => setIsRemovedMembersModalOpen(false)}
          removedMembers={removedMembers}
        />

      </IonContent>
    </IonPage >
  );
}
